import { Alert, Anchor, Badge, Button, Group, Paper, Progress, Stack, Text, Title, UnstyledButton } from "@mantine/core";
import { IconAlertTriangle, IconArrowsExchange, IconChevronRight, IconPlayerPlay } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useDashboard, type Dashboard } from "../../api/dashboard";
import { formatQuantity } from "../catalog/format";
import { TaskCard } from "../tasks/TaskCard";

/** Sekce Činnost (F-DSH-01, 03): START, stav kontroly, aktuální úkoly, kritické zásoby, přesuny; manažerský přehled. */
export function ActivityPage() {
  const { t, i18n } = useTranslation();
  const dashboard = useDashboard();
  const d = dashboard.data;

  return (
    <Stack>
      <Stack gap={0}>
        <Title order={2}>{t("nav.activity")}</Title>
        {d && (
          <Text c="dimmed" size="sm">
            {new Date(d.businessDate).toLocaleDateString(i18n.language, { weekday: "long", day: "numeric", month: "long" })}
          </Text>
        )}
      </Stack>
      {d && !d.isOpen && <Alert color="gray">{t("activity.closedToday")}</Alert>}

      <Button component={Link} to="/start" size="xl" h={64} leftSection={<IconPlayerPlay />}>
        START
      </Button>

      {d && d.checks.some((c) => c.mine) && (
        <Section title={t("check.title")} to="/kontrola">
          {d.checks.filter((c) => c.mine).map((check) => (
            <UnstyledButton key={check.warehouseId} component={Link} to={`/kontrola/sklad/${check.warehouseId}`}>
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Text size="sm" fw={500} style={{ flex: 1 }}>
                  {check.name}
                </Text>
                <Progress value={check.total ? (check.answered / check.total) * 100 : 0} w={90} radius="xl"
                  color={check.status === "Completed" ? "green" : "blue"} />
                <Text size="xs" c="dimmed" w={44} ta="right">
                  {check.answered}/{check.total}
                </Text>
              </Group>
            </UnstyledButton>
          ))}
        </Section>
      )}

      {d && (
        <Section title={t("activity.currentTasks")} to="/ukoly"
          extra={
            <Text size="xs" c="dimmed">
              {t("activity.taskCounts", { work: d.taskCounts.workOpen, prep: d.taskCounts.prepOpen })}
            </Text>
          }>
          {d.currentTasks.length === 0 && <Text size="sm" c="dimmed">{t("activity.allDone")}</Text>}
          {d.currentTasks.map((task) => (
            <TaskCard key={task.id} task={task} />
          ))}
        </Section>
      )}

      {d && d.critical.length > 0 && (
        <UnstyledButton component={Link} to="/sklady?tab=kriticke">
          <Alert color="red" icon={<IconAlertTriangle />} title={t("activity.critical", { count: d.critical.length })}>
            {d.critical.slice(0, 4).map((c) => `${c.name} (${formatQuantity(c.total, i18n.language)} ${c.unit})`).join(", ")}
            {d.critical.length > 4 && " …"}
          </Alert>
        </UnstyledButton>
      )}

      {d && d.openTransfers > 0 && (
        <UnstyledButton component={Link} to="/sklady">
          <Alert color="blue" icon={<IconArrowsExchange />}>{t("activity.openTransfers", { count: d.openTransfers })}</Alert>
        </UnstyledButton>
      )}

      {d?.manager && <ManagerBlock manager={d.manager} />}

      <Anchor component={Link} to="/souhrn" ta="center">
        {t("summary.title")}
      </Anchor>
    </Stack>
  );
}

function Section({ title, to, extra, children }: { title: string; to: string; extra?: ReactNode; children: ReactNode }) {
  return (
    <Paper withBorder p="sm">
      <Stack gap="xs">
        <UnstyledButton component={Link} to={to}>
          <Group justify="space-between" wrap="nowrap">
            <Stack gap={0}>
              <Text fw={700}>{title}</Text>
              {extra}
            </Stack>
            <IconChevronRight size={18} />
          </Group>
        </UnstyledButton>
        {children}
      </Stack>
    </Paper>
  );
}

/** Manažerský přehled a závěr dne (F-DSH-03, 5.11): plnění po odděleních, fronta ke kontrole, nákup, upozornění. */
function ManagerBlock({ manager }: { manager: NonNullable<Dashboard["manager"]> }) {
  const { t } = useTranslation();
  return (
    <Paper withBorder p="sm">
      <Stack gap="xs">
        <Group justify="space-between">
          <Text fw={700}>{t("activity.manager.title")}</Text>
          <Text fw={700}>
            {manager.tasksDone} / {manager.tasksTotal}
          </Text>
        </Group>
        <Progress value={manager.tasksTotal ? (manager.tasksDone / manager.tasksTotal) * 100 : 0} radius="xl" size="lg" />
        {manager.byDepartment.map((d) => (
          <Group key={d.departmentName ?? "-"} justify="space-between">
            <Text size="sm">{d.departmentName ?? t("tasks.unassigned")}</Text>
            <Text size="sm" c="dimmed">
              {d.done} / {d.total}
            </Text>
          </Group>
        ))}
        <Group gap="xs" mt={4}>
          <Button component={Link} to="/ukoly/kontrola" size="xs" variant="light" rightSection={<Badge size="xs" circle>{manager.toReview}</Badge>}>
            {t("tasks.review.title")}
          </Button>
          <Button component={Link} to="/sklady?tab=nakup" size="xs" variant="light" rightSection={<Badge size="xs" circle>{manager.shoppingOpen}</Badge>}>
            {t("stock.tabs.shopping")}
          </Button>
        </Group>
        {manager.unfinishedChecks.length > 0 && (
          <Text size="sm" c="orange.8">
            {t("activity.manager.unfinishedChecks", { names: manager.unfinishedChecks.join(", ") })}
          </Text>
        )}
        {manager.missedYesterday > 0 && (
          <Text size="sm" c="red.7">
            {t("activity.manager.missedYesterday", { count: manager.missedYesterday })}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
